import {
  Phone,
  Video,
  MoreVertical,
  ChevronLeft,
  Paperclip,
  Mic,
  Smile,
  CheckCheck,
  Zap,
  CheckCircle2,
  Lock,
} from 'lucide-react'

export default function HeroPhoneMockup() {
  return (
    <div className="relative w-full max-w-[340px] sm:max-w-[390px] mx-auto select-none">
      {/* Glow background behind phone */}
      <div className="absolute -inset-2 bg-gradient-to-tr from-emerald-500/25 via-teal-400/20 to-emerald-300/20 rounded-[3rem] blur-2xl opacity-75 pointer-events-none" />

      {/* Floating Badges */}
      <div className="hidden sm:flex absolute -left-6 top-10 z-20 items-center gap-2 bg-white/95 backdrop-blur-md px-3.5 py-2 rounded-2xl border border-emerald-200/90 shadow-xl shadow-emerald-900/10 text-xs font-bold text-gray-800 animate-bounce duration-1000">
        <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <Zap className="w-3.5 h-3.5" />
        </div>
        <div>
          <div className="text-[10px] text-gray-600 font-medium leading-none">Respuesta con IA</div>
          <div className="text-emerald-700 font-black">&lt; 500 ms</div>
        </div>
      </div>

      <div className="hidden sm:flex absolute -right-6 bottom-16 z-20 items-center gap-2 bg-white/95 backdrop-blur-md px-3.5 py-2 rounded-2xl border border-emerald-200/90 shadow-xl shadow-emerald-900/10 text-xs font-bold text-gray-800">
        <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <CheckCircle2 className="w-3.5 h-3.5" />
        </div>
        <div>
          <div className="text-[10px] text-gray-600 font-medium leading-none">Reglas de Negocio</div>
          <div className="text-emerald-700 font-black">Stock 100% en vivo</div>
        </div>
      </div>

      {/* Phone Hardware Chassis */}
      <div className="relative rounded-[2.8rem] bg-slate-900 p-2.5 sm:p-3 shadow-2xl ring-1 ring-white/20 border-4 border-slate-700/80">
        {/* Dynamic Island / Notch */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-28 h-5 bg-black rounded-full z-30 flex items-center justify-between px-3">
          <div className="w-2.5 h-2.5 rounded-full bg-slate-900 border border-slate-800" />
          <div className="w-2 h-2 rounded-full bg-emerald-500/80 animate-pulse" />
        </div>

        {/* Screen Glass */}
        <div className="relative rounded-[2.3rem] overflow-hidden bg-[#efeae2] flex flex-col h-[580px] sm:h-[620px] shadow-inner text-slate-800 font-sans">
          {/* WhatsApp Header */}
          <div className="bg-[#075e54] text-white pt-8 pb-3 px-3 shadow-md z-10 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <ChevronLeft className="w-5 h-5 text-white/90 shrink-0" />
                {/* Avatar with Bot badge */}
                <div className="relative w-9 h-9 rounded-full bg-white border border-white/40 flex items-center justify-center text-emerald-800 font-bold shrink-0 shadow-sm overflow-hidden p-1">
                  <img
                    src="/new/IridiaChat_Isotipo_Icono_Transparente.png"
                    alt="IridiaChat"
                    className="w-full h-full object-contain"
                  />
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 border-2 border-[#075e54] rounded-full" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 leading-tight">
                    <span className="text-sm font-bold truncate">IridiaChat Tech</span>
                    <span className="bg-emerald-400/20 text-emerald-200 text-[9px] font-extrabold px-1.5 py-0.2 rounded-full border border-emerald-400/30 uppercase">
                      OFICIAL
                    </span>
                  </div>
                  <div className="text-[11px] text-emerald-100/90 truncate flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" />
                    <span>en línea · catálogo activo</span>
                  </div>
                </div>
              </div>

              {/* Action Icons */}
              <div className="flex items-center gap-3 text-white/90 shrink-0">
                <Video className="w-4 h-4" />
                <Phone className="w-4 h-4" />
                <MoreVertical className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Chat Body with WhatsApp Background Style */}
          <div className="flex-1 p-3 space-y-2.5 overflow-y-auto text-xs leading-relaxed bg-[#efeae2] bg-opacity-95">
            {/* Encryption Notice */}
            <div className="flex justify-center my-1">
              <div className="bg-[#ffeecd] text-amber-900 border border-amber-300/60 rounded-lg px-2.5 py-1 text-[10px] text-center max-w-[92%] shadow-2xs flex items-center justify-center gap-1">
                <Lock className="w-3 h-3 text-amber-700 shrink-0" />
                <span>Los mensajes están cifrados de extremo a extremo.</span>
              </div>
            </div>

            {/* Bubble 1: Cliente consulta iPhone 15 */}
            <div className="flex justify-end">
              <div className="bg-[#d9fdd3] text-gray-900 rounded-2xl rounded-tr-xs px-3 py-2 max-w-[85%] shadow-2xs border border-emerald-200/60">
                <p className="text-[12px]">
                  ¡Hola! ¿Tienen disponible el <strong>iPhone 15 Pro Max de 256GB</strong>? ¿Qué colores tienen y qué precio?
                </p>
                <div className="flex items-center justify-end gap-1 text-[10px] text-gray-500 mt-1">
                  <span>10:24 AM</span>
                  <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
                </div>
              </div>
            </div>

            {/* Bubble 2: Bot IridiaChat responde con catálogo real */}
            <div className="flex justify-start">
              <div className="bg-white text-gray-900 rounded-2xl rounded-tl-xs px-3 py-2 max-w-[92%] shadow-2xs border border-gray-200/70">
                <p className="text-[12px] mb-1.5">
                  ¡Hola! 👋 Sí, contamos con disponibilidad inmediata en inventario:
                </p>

                {/* Product Mini-Card */}
                <div className="bg-emerald-50/70 border border-emerald-200/90 rounded-xl p-2 mb-2">
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className="font-bold text-gray-900 text-[12px]">iPhone 15 Pro Max (256 GB)</span>
                    <span className="bg-emerald-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded-md">
                      DISPONIBLE
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-600 mb-1">
                    🎨 Colores: <strong>Titanio Natural</strong> y <strong>Titanio Negro</strong>
                  </div>
                  <div className="flex items-baseline justify-between pt-1 border-t border-emerald-200/50">
                    <span className="text-[10px] text-gray-500 font-medium">Precio oficial:</span>
                    <span className="text-sm font-black text-emerald-800">$1,190.00 USD</span>
                  </div>
                  <div className="text-[10px] text-emerald-700 font-medium">
                    Inventario en tiempo real · Garantía de tienda
                  </div>
                </div>

                <p className="text-[11px] text-gray-700">
                  📦 <strong>¿Te gustaría apartarlo</strong> para retiro en tienda hoy o prefieres delivery express asegurado?
                </p>
                <div className="flex items-center justify-end text-[10px] text-gray-400 mt-1">
                  <span>10:24 AM</span>
                </div>
              </div>
            </div>

            {/* Bubble 3: Cliente confirma compra */}
            <div className="flex justify-end">
              <div className="bg-[#d9fdd3] text-gray-900 rounded-2xl rounded-tr-xs px-3 py-2 max-w-[85%] shadow-2xs border border-emerald-200/60">
                <p className="text-[12px]">
                  ¡Excelente! Lo quiero en <strong>Titanio Natural</strong> con delivery a Los Palos Grandes. ¿Cómo hago el pago?
                </p>
                <div className="flex items-center justify-end gap-1 text-[10px] text-gray-500 mt-1">
                  <span>10:25 AM</span>
                  <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
                </div>
              </div>
            </div>

            {/* System Handoff Notification */}
            <div className="flex justify-center my-0.5">
              <div className="bg-emerald-100/90 text-emerald-900 border border-emerald-300 rounded-full px-3 py-1 text-[10px] font-bold shadow-2xs flex items-center gap-1.5">
                <Zap className="w-3 h-3 text-emerald-700" />
                <span>Orden generada · Transferido a asesor humano</span>
              </div>
            </div>

            {/* Bubble 4: Asesor humano toma el caso */}
            <div className="flex justify-start">
              <div className="bg-white text-gray-900 rounded-2xl rounded-tl-xs px-3 py-2 max-w-[90%] shadow-2xs border border-gray-200/70">
                <div className="text-[10px] font-bold text-emerald-800 mb-0.5 flex items-center gap-1">
                  <span>Ana (Asesora Comercial)</span>
                </div>
                <p className="text-[11px] text-gray-700">
                  ¡Hola! Tu pedido ya está apartado en el sistema. Te pasé por aquí los datos de Pago Móvil y cuenta en divisas para coordinar el motorizado.
                </p>
                <div className="flex items-center justify-end text-[10px] text-gray-400 mt-1">
                  <span>10:25 AM</span>
                </div>
              </div>
            </div>
          </div>

          {/* WhatsApp Bottom Input Bar */}
          <div className="bg-[#f0f2f5] px-2.5 py-2 flex items-center gap-2 border-t border-gray-200 shrink-0">
            <Smile className="w-5 h-5 text-gray-500 shrink-0" />
            <div className="flex-1 bg-white rounded-full px-3.5 py-1.5 text-xs text-gray-400 border border-gray-200 shadow-inner flex items-center justify-between">
              <span>Escribe un mensaje...</span>
              <Paperclip className="w-4 h-4 text-gray-400" />
            </div>
            <div className="w-8 h-8 rounded-full bg-[#00a884] text-white flex items-center justify-center shrink-0 shadow-sm">
              <Mic className="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
